import type { DraftOutcome } from "../api/types.js";

/**
 * DCTA-188 Change 4 (docs/specs/fe-validator-freeze-dcta188.md; docs/specs/demo-validator-freeze.md, beat 5 of the video):
 * judge mode's "Faulty drafter (demo)" switch. While it is on, the NEXT request goes to `POST /v1/demo/faulty-drafter`,
 * where the bank swaps the drafter's two amounts on purpose so the independent reading can be seen catching it; then the
 * switch is off by itself. Judge mode only (the route exists only with DEMO_MODE=1 and the model path); nothing it
 * produces can be signed — the route never returns a card.
 */
export const DEMO_FAULT = "swap_amounts" as const;

/** The app's own lines for a demo answer that caught nothing (`kind: "demo_result"`), the spec's words. */
export const DEMO_FAULT_LINES = {
  nothingToSwap: "Demo: nothing to swap in that sentence. Say two payments with different amounts.",
  agreed: "Demo: the validator agreed with the altered draft, so nothing was drafted.",
} as const;

export class DemoFaultSwitch {
  #armed = false;
  readonly #listeners = new Set<(on: boolean) => void>();

  get armed(): boolean {
    return this.#armed;
  }

  set(on: boolean): void {
    if (on === this.#armed) return;
    this.#armed = on;
    for (const fn of [...this.#listeners]) fn(on);
  }

  /** One message: true once while it is on — and it is off from then on. */
  take(): boolean {
    if (!this.#armed) return false;
    this.set(false);
    return true;
  }

  /** Called on every change (on; off by hand or after its one message). Returns the way to stop listening. */
  onChange(fn: (on: boolean) => void): () => void {
    this.#listeners.add(fn);
    return () => void this.#listeners.delete(fn);
  }
}

/** The bank's label on a demo answer ("Demo: the drafter's answer was altered on purpose …"), verbatim; else none. */
export function demoLabelOf(body: unknown): string | undefined {
  const fault = typeof body === "object" && body !== null ? (body as { demo_fault?: unknown }).demo_fault : undefined;
  const label = typeof fault === "object" && fault !== null ? (fault as { label?: unknown }).label : undefined;
  return typeof label === "string" && label !== "" ? label : undefined;
}

/** A demo answer that caught nothing: the fault did not apply (nothing to swap) or the validator agreed with it. */
export function demoResultLine(outcome: DraftOutcome): string | undefined {
  if (outcome.kind !== "demo_result") return undefined;
  return outcome.demo_fault?.applied === true ? DEMO_FAULT_LINES.agreed : DEMO_FAULT_LINES.nothingToSwap;
}
